import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getJourneyState, getMyProfile } from "@/lib/journey.functions";
import { GATES } from "@/lib/gates";
import { Card } from "@/components/ui/card";
import { RecommendationsRail } from "@/components/site/RecommendationsRail";
import { TodayActivation } from "@/components/site/TodayActivation";

export const Route = createFileRoute("/_authenticated/journey/")({
  component: JourneyOverview,
});

function JourneyOverview() {
  const load = useServerFn(getJourneyState);
  const loadProfile = useServerFn(getMyProfile);
  const [state, setState] = useState<Awaited<ReturnType<typeof getJourneyState>> | null>(null);
  const [profile, setProfile] = useState<Awaited<ReturnType<typeof getMyProfile>> | null>(null);

  useEffect(() => {
    load().then(setState).catch(() => setState(null));
    loadProfile().then(setProfile).catch(() => setProfile(null));
  }, [load, loadProfile]);

  const completions = state?.completions.length ?? 0;
  const challengesActive = state?.challenges.filter((c) => !c.completed_at && !c.abandoned_at).length ?? 0;
  const certs = state?.certificates.length ?? 0;
  const currentGateN = profile?.current_gate ?? null;
  const currentGate = currentGateN ? GATES.find((g) => g.n === currentGateN) : undefined;

  return (
    <>
      <TodayActivation />
      <div className="grid gap-6 md:grid-cols-3">
        <Card className="p-5 md:col-span-2">
          <h2 className="font-serif text-xl text-gold-luminous">Where you stand today</h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Activations" value={completions} />
            <Stat label="Active challenges" value={challengesActive} />
            <Stat label="Certificates" value={certs} />
            <Stat label="Badges" value={state?.badges.length ?? 0} />
          </div>
          {currentGate && (
            <div className="mt-6 rounded-md border hairline p-4">
              <div className="text-xs uppercase tracking-widest text-foreground/60">Current Gate</div>
              <div className="mt-1 font-serif text-lg text-gold-luminous">Gate {currentGate.n} · {currentGate.title}</div>
              <Link to="/gates/$n" params={{ n: String(currentGate.n) }} className="mt-2 inline-block text-sm text-gold underline underline-offset-4">Open this gate →</Link>
            </div>
          )}
        </Card>
        <Card className="p-5">
          <h2 className="font-serif text-xl text-gold-luminous">Move next</h2>
          <ul className="mt-4 space-y-3 text-sm">
            <li><Link to="/journey/map" className="text-gold hover:underline">Open your Life Journey Map →</Link></li>
            <li><Link to="/journey/timeline" className="text-gold hover:underline">See your Timeline →</Link></li>
            <li><Link to="/journey/challenges" className="text-gold hover:underline">Browse guided challenges →</Link></li>
            <li><Link to="/journey/journal" className="text-gold hover:underline">Write a reflection →</Link></li>
          </ul>
        </Card>
      </div>
      <RecommendationsRail limit={4} heading="Suggested for you" />
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-2xl font-medium text-gold-luminous">{value}</div>
      <div className="text-xs text-foreground/60">{label}</div>
    </div>
  );
}
